import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import React, { useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Heading6 } from '@ui/Typography'
import { IconButton } from '@ui/components/Button'
import { CloseIcon } from '@ui/icons/Close.icon'
import { DummyIcon } from '@ui/icons/Dummy.icon'

import { ResponseError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { wait } from '@utils/wait'

import { FreeTextAnswer, IBTestSubCategory } from '@model/TestSectionsDto'

import { useIBLink } from '@hooks/useIBLink'
import { useIBReferralCodes } from '@hooks/useIBReferralCodes'
import { ScrollToIds, useScrollToElementIds } from '@hooks/useScrollToElementIds'

import { Loading } from '@shared/components/Loading/Loading'

import { IBCarousel } from '../Carousel/IBCarousel'
import { IBQuestionnaire } from './IBQuestionnaire'

import styles from './IBQuestionnairePage.module.scss'

export const IBQuestionnairePage: React.FC = () => {
  const { t } = useTranslation()
  const { subCategoryId } = useParams<{ subCategoryId: IBTestSubCategory }>()
  const { account } = useAccountReadContext()
  const { refreshReferralCodes } = useIBReferralCodes()
  const { refreshIBLinks } = useIBLink()

  const { scrollIntoView } = useScrollToElementIds()
  const [isLoading, setIsLoading] = useState(false)

  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const navigate = useNavigate()

  const { data: ibSignUpTest, isLoading: isLoadingIBSignUpTest } = useQuery({
    queryKey: queryKeys.ib.signUpTest(locale, subCategoryId),
    queryFn: () => apiClient.getIBSignUpTest(locale, subCategoryId!),
    enabled: !!subCategoryId,
  })

  const { data: legalDocuments = [], isLoading: isLoadingLegalDocuments } = useQuery({
    queryKey: queryKeys.ib.signUpLegalDocuments(locale, subCategoryId),
    queryFn: () => {
      const ibScheme =
        subCategoryId === IBTestSubCategory.Default
          ? '0'
          : subCategoryId === IBTestSubCategory.MultiTier
          ? '1'
          : ''
      return apiClient.getIntroducingBrokerLegalDocuments(
        locale,
        ibScheme ? `&IbSchemes=${ibScheme}` : ''
      )
    },
  })

  const handleSignUpSubmit = async (selectedAnswers: string[], freeAnswers: FreeTextAnswer[]) => {
    try {
      setIsLoading(true)
      scrollIntoView([ScrollToIds.IBSignupHeader, ScrollToIds.IBProfileHeader])
      if (!account || !ibSignUpTest?.sections[0].questions) {
        return
      }

      await apiClient.signUpIntroducingBroker({
        category: 'IB information',
        clientId: account?.id,
        testId: ibSignUpTest?.id,
        dateTaken: new Date(),
        selectedAnswers,
        freeAnswers,
      })

      await wait()
      refreshReferralCodes()
      refreshIBLinks()
      navigate('../thank-you', { state: { fromQuestionnaire: true } })
    } catch (e: ResponseError | any) {
      const error = e as ResponseError
      console.error(error.response.response)
    } finally {
      setIsLoading(false)
    }
  }

  useLayoutEffect(() => {
    scrollIntoView([ScrollToIds.IBProfileHeader])
  }, [isLoadingLegalDocuments, scrollIntoView])

  return (
    <div className={styles.questionnaire}>
      <div className={styles.titleWrapper}>
        <IconButton.Ghost
          icon={CloseIcon}
          onClick={() => {
            navigate('../../type')
            scrollIntoView([ScrollToIds.IBSignupHeader, ScrollToIds.IBProfileHeader])
          }}
        />
        <Heading6 className={styles.title} medium>
          {subCategoryId === IBTestSubCategory.MultiTier
            ? t('IB.Multitier Program')
            : t('IB.Introducing Broker Program')}
        </Heading6>
        <DummyIcon />
      </div>
      <Loading
        isLoading={isLoadingIBSignUpTest || isLoadingLegalDocuments || isLoading}
        showLoadingIcon
      >
        <div className={styles.wrapper}>
          {!!ibSignUpTest && (
            <div className={styles.formInputWrapper}>
              <IBQuestionnaire
                legalDocuments={legalDocuments}
                testData={ibSignUpTest}
                onSubmit={handleSignUpSubmit}
                isMultiTier={subCategoryId === IBTestSubCategory.MultiTier}
              />
            </div>
          )}
          <IBCarousel subCategoryId={subCategoryId} />
        </div>
      </Loading>
    </div>
  )
}
